import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Newspaper, FolderOpen, FileText, Image, Users, MessageSquare, ArrowRight, TrendingUp } from 'lucide-react';
import { noticiaService, projetoService, documentoService, galeriaService, membroService, contatoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './AdminDashboard.css';

interface Stats { noticias:number; projetos:number; documentos:number; galeria:number; membros:number; contatos:number; }

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats,   setStats]   = useState<Stats>({ noticias:0, projetos:0, documentos:0, galeria:0, membros:0, contatos:0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.allSettled([
      noticiaService.listarAdmin(),
      projetoService.listar(),
      documentoService.listar(),
      galeriaService.listar(),
      membroService.listar(),
      contatoService.listar(),
    ]).then(results => {
      const get = (r: PromiseSettledResult<any>, key: string) => {
        if (r.status === 'fulfilled') {
          const d = r.value.data;
          return d?.total ?? (Array.isArray(d) ? d.length : (d?.[key]?.length ?? 0));
        }
        return 0;
      };
      setStats({
        noticias:  get(results[0], 'noticias'),
        projetos:  get(results[1], 'projetos'),
        documentos:get(results[2], 'documentos'),
        galeria:   get(results[3], 'fotos'),
        membros:   get(results[4], 'membros'),
        contatos:  get(results[5], 'contatos'),
      });
      setLoading(false);
    });
  }, []);

  const cards = [
    { label: 'Notícias',    value: stats.noticias,   icon: <Newspaper size={22}/>,     to: '/admin/noticias',   color: '#fde8d8', accent: 'var(--laranja)' },
    { label: 'Projetos',    value: stats.projetos,   icon: <FolderOpen size={22}/>,    to: '/admin/projetos',   color: '#daf2f0', accent: 'var(--turquesa)' },
    { label: 'Documentos',  value: stats.documentos, icon: <FileText size={22}/>,      to: '/admin/documentos', color: '#fdf5d0', accent: '#d4a017' },
    { label: 'Galeria',     value: stats.galeria,    icon: <Image size={22}/>,         to: '/admin/galeria',    color: '#fde0e7', accent: 'var(--rosa)' },
    { label: 'Membros',     value: stats.membros,    icon: <Users size={22}/>,         to: '/admin/membros',    color: '#ede8fe', accent: '#7c3aed' },
    { label: 'Contatos',    value: stats.contatos,   icon: <MessageSquare size={22}/>, to: '/admin/contatos',   color: '#d1fae5', accent: '#059669' },
  ];

  const atalhos = [
    { label: 'Nova Notícia',   to: '/admin/noticias/novo',   icon: <Newspaper size={16}/> },
    { label: 'Novo Projeto',   to: '/admin/projetos/novo',   icon: <FolderOpen size={16}/> },
    { label: 'Novo Documento', to: '/admin/documentos/novo', icon: <FileText size={16}/> },
    { label: 'Add Foto',       to: '/admin/galeria/novo',    icon: <Image size={16}/> },
  ];

  return (
    <div>
      {/* Welcome */}
      <div className="dash-welcome">
        <div>
          <h1>Olá, {user?.nome?.split(' ')[0] || 'Admin'} 👋</h1>
          <p>Bem-vindo ao painel de gestão do Instituto Mãos Estendidas.</p>
        </div>
        <div className="dash-date">
          {new Date().toLocaleDateString('pt-BR', { weekday:'long', day:'2-digit', month:'long', year:'numeric' })}
        </div>
      </div>

      {/* Stats */}
      <div className="dash-stats">
        {cards.map((c, i) => (
          <Link to={c.to} className="dash-stat-card" key={i} style={{ '--accent': c.accent, '--bg': c.color } as React.CSSProperties}>
            <div className="dsc-icon" style={{ background: c.color, color: c.accent }}>{c.icon}</div>
            <div className="dsc-body">
              <span className="dsc-label">{c.label}</span>
              {loading
                ? <span className="dsc-num" style={{ opacity: 0.3 }}>—</span>
                : <span className="dsc-num">{c.value}</span>
              }
            </div>
            <ArrowRight size={16} className="dsc-arrow" />
          </Link>
        ))}
      </div>

      {/* Quick actions */}
      <div className="dash-section">
        <div className="dash-section-header">
          <TrendingUp size={18} />
          <h3>Ações rápidas</h3>
        </div>
        <div className="dash-atalhos">
          {atalhos.map((a, i) => (
            <Link to={a.to} className="dash-atalho" key={i}>
              {a.icon}
              {a.label}
            </Link>
          ))}
        </div>
      </div>

      {/* Info */}
      <div className="dash-info-bar">
        <div className="dash-info-item">
          <span>CNPJ</span>
          <strong>45.334.670/0001-82</strong>
        </div>
        <div className="dash-info-item">
          <span>Backend</span>
          <strong>Render (Node.js)</strong>
        </div>
        <div className="dash-info-item">
          <span>Banco de dados</span>
          <strong>MongoDB Atlas</strong>
        </div>
        <div className="dash-info-item">
          <span>Storage</span>
          <strong>Cloudinary</strong>
        </div>
      </div>
    </div>
  );
}
